import React, { useState, useEffect } from 'react';
import { Typography, Box, IconButton } from '@mui/material';
import { Delete as DeleteIcon, AccessTime as AccessTimeIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { LAST_MONTH_TOGGLE_BUTTON_SX } from './sections.style';
import { BookmarkToggle } from './BookmarkToggle';

interface LastMonthFieldToggleProps {
  lastMonthValue?: string;
  label: string;
  reserveTrailingActionSpace?: boolean;
}

interface LastMonthReadonlyPanelProps {
  value: string;
}

// Reusable read-only panel for displaying last month's text content
export const LastMonthReadonlyPanel: React.FC<LastMonthReadonlyPanelProps> = ({ value }) => {
  if (!value) return null;

  return (
    <Box
      sx={{
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'grey.200',
        backgroundColor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'grey.200',
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          px: 1.5,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Typography
          variant='caption'
          sx={{
            color: 'text.secondary',
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            fontWeight: 500,
          }}
        >
          Last month
        </Typography>
      </Box>
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: 'grey.50',
          borderBottomLeftRadius: 10,
          borderBottomRightRadius: 10,
        }}
      >
        <Typography variant='body2' sx={{ fontSize: '0.9rem', color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Reusable component for showing/hiding last month field values
 * Displays a toggle link and shows the last month value when toggled
 */
export const LastMonthFieldToggle: React.FC<LastMonthFieldToggleProps> = ({
  lastMonthValue,
  label,
  reserveTrailingActionSpace = false,
}) => {
  const [showLastMonth, setShowLastMonth] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  if (!lastMonthValue) {
    return null;
  }

  const handleToggle = () => {
    setShowLastMonth(!showLastMonth);
  };

  return (
    <Box>
      <Box
        onClick={handleToggle}
        sx={{
          ...LAST_MONTH_TOGGLE_BUTTON_SX,
          color: showLastMonth ? 'primary.main' : 'text.secondary',
          '&:hover': {
            backgroundColor: 'transparent',
            color: showLastMonth ? 'primary.dark' : 'text.primary',
          },
        }}
      >
        <AccessTimeIcon
          sx={{
            width: '0.8rem',
            height: '0.8rem',
          }}
        />
        <Typography
          variant='caption'
          sx={{
            fontWeight: 600,
            textTransform: 'none',
            letterSpacing: 0.2,
            color: 'inherit',
          }}
        >
          {showLastMonth ? 'Hide last month' : 'Show last month'}
        </Typography>
      </Box>
      {showLastMonth && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            {/* This Box matches FieldRow's inner Box - flexGrow: 1, width: '100%' */}
            <Box sx={{ flexGrow: 1, width: '100%' }}>
              {/* This Box matches the structure inside FieldRow's children - flex display */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexGrow: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Box
                    sx={{
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      backgroundColor: 'background.paper',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: 'grey.200',
                        borderTopLeftRadius: 10,
                        borderTopRightRadius: 10,
                        px: 1.5,
                        py: 0.5,
                        borderBottom: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      <Typography
                        variant='caption'
                        sx={{
                          color: 'text.secondary',
                          letterSpacing: 0.4,
                          textTransform: 'uppercase',
                          fontWeight: 500,
                        }}
                      >
                        Last month
                      </Typography>
                      <Box
                        component='button'
                        type='button'
                        onClick={e => {
                          e.stopPropagation();
                          if (!lastMonthValue) return;
                          if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                            navigator.clipboard
                              .writeText(lastMonthValue)
                              .then(() => setCopied(true))
                              .catch(() => undefined);
                          }
                        }}
                        style={{
                          border: 'none',
                          background: 'none',
                          padding: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          cursor: 'pointer',
                          color: copied ? 'rgba(37, 99, 235, 0.7)' : '#64748b',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                        {copied ? 'Copied!' : 'Use this'}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        bgcolor: 'grey.50',
                        borderBottomLeftRadius: 10,
                        borderBottomRightRadius: 10,
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{ fontSize: '0.9rem', color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
                      >
                        {lastMonthValue}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {reserveTrailingActionSpace && (
                  <IconButton size='small' sx={{ mt: 0.5, visibility: 'hidden', flexShrink: 0 }}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </Box>
            {/* Invisible BookmarkToggle spacer to match FieldRow's right-side bookmark */}
            <Box sx={{ pt: 0.75, visibility: 'hidden', flexShrink: 0 }}>
              <BookmarkToggle addToActionItems={false} onToggle={() => undefined} />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
